import { useMemo } from "react"

import { Button, Select, Switch } from "@productlane/ui"

interface ContactsSubscribedStepProps {
	data: string[][]
	hasHeaders: boolean
	back: () => void
	next: () => void
	subscribedIndex: number
	setSubscribedIndex: (index: number) => void
	subscribeUnmapped: boolean
	setSubscribeUnmapped: (value: boolean) => void
	sortedData: string[][]
}

export function ContactsSubscribedStep({
	data,
	hasHeaders,
	back,
	next,
	subscribedIndex,
	setSubscribedIndex,
	subscribeUnmapped,
	setSubscribeUnmapped,
	sortedData,
}: ContactsSubscribedStepProps) {
	const headers = useMemo(() => {
		if (!data[0]) {
			return []
		}
		if (hasHeaders) {
			return data[0].map((it, i) => ({
				label: it,
				value: i.toString(),
			}))
		}
		return data[0].map((_, i) => ({
			label: `${i + 1}. Column`,
			value: i.toString(),
		}))
	}, [hasHeaders, data])

	const values = useMemo(() => {
		if (subscribedIndex === -1) return []
		const col = sortedData[subscribedIndex] ?? []
		return col.slice(hasHeaders ? 1 : 0)
	}, [hasHeaders, sortedData, subscribedIndex])

	const isValid = useMemo(() => {
		if (subscribedIndex === -1) return true
		for (const v of values) {
			if (!isBooleanLoose((v ?? "").trim())) {
				return false
			}
		}
		return true
	}, [subscribedIndex, values])

	const preview = useMemo(() => values.slice(0, 6), [values])

	return (
		<div>
			<Select
				value={subscribedIndex.toString()}
				label="Select the column of the subscribed flag (optional)"
				name="contact-is-subscribed"
				options={[emptyState("No subscribed flag"), ...headers]}
				onChange={(it) => setSubscribedIndex(Number(it))}
				showErrorMessage={!isValid}
				errorMessage={
					isValid ? undefined : "Invalid values found (use true/false)"
				}
			/>

			<div className="mt-4 flex items-start justify-between gap-4 rounded-lg border border-gray-200 p-3 dark:border-gray-700/80">
				<div>
					<div className="text-sm text-primary">
						Subscribe contacts to changelog emails
					</div>
					<div className="mt-0.5 text-xs text-secondary">
						{subscribedIndex === -1
							? "Applies to everyone in this file, since no column is mapped."
							: "Applies to rows where the column is empty."}{" "}
						They receive changelog and product update emails until they
						unsubscribe.
					</div>
				</div>
				<Switch
					name="subscribe-unmapped-contacts"
					checked={subscribeUnmapped}
					onCheckedChange={setSubscribeUnmapped}
				/>
			</div>

			<div className="mb-2 mt-4 text-sm text-primary">Preview:</div>
			<div className="relative mt-1 overflow-hidden rounded-lg border bg-white p-2 dark:bg-gray-900">
				{subscribedIndex === -1 ? (
					<div className="text-xs italic text-secondary">No Preview</div>
				) : (
					<div className="flex flex-col gap-1">
						{preview.map((it, i) => (
							<div
								key={`sub-${i}`}
								className={
									isBooleanLoose((it ?? "").trim())
										? "truncate text-xs text-secondary"
										: "truncate text-xs text-red-500"
								}
							>
								{it}
							</div>
						))}
					</div>
				)}
				{subscribedIndex !== -1 && (
					<div className="absolute inset-x-0 bottom-0 h-[80%] bg-gradient-to-b from-transparent via-white/80 to-white dark:via-brand-900/80 dark:to-brand-900" />
				)}
			</div>

			<div className="mt-2 flex items-center justify-between">
				<Button variant="link" onClick={back}>
					Back
				</Button>
				<Button onClick={next} disabled={!isValid}>
					Select
				</Button>
			</div>
		</div>
	)
}

const emptyState = (label: string) => ({
	label,
	value: "-1",
})

const isBooleanLoose = (value: string): boolean => {
	if (value === "") return true
	const v = value.toLowerCase()
	return ["true", "false", "1", "0", "yes", "no", "y", "n"].includes(v)
}
